import React from "react";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_DOWN_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextNode,
  UNDO_COMMAND,
  type LexicalEditor,
  type RangeSelection,
} from "lexical";
import { mergeRegister } from "@lexical/utils";
import { useT } from "../../i18n";
import { EDITOR_VOCABULARY } from "./rich-text-vocabulary";
import reviewStyles from "./email-review.module.css";

export interface RichTextEditorValue {
  html: string;
  text: string;
  changed: boolean;
}

function isSafeHttpUrl(candidate: string): boolean {
  try {
    const parsed = new URL(candidate.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function plainTextHtml(text: string): string {
  const escape = (value: string) => value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const html = text.replace(/\r\n?/g, "\n")
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escape(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
  return html || "<p></p>";
}

/** Client-side preview cleanup; the server repeats this from hostile input and remains authority. */
function normalizeExportedHtml(source: string): string {
  const document = new DOMParser().parseFromString(`<body>${source}</body>`, "text/html");
  for (const nested of Array.from(document.body.querySelectorAll("li ul, li ol"))) {
    const parentItem = nested.parentElement?.closest("li");
    const outerList = parentItem?.parentElement;
    if (!parentItem || !outerList) { nested.remove(); continue; }
    let cursor: Element = parentItem;
    for (const child of Array.from(nested.children)) {
      if (child.tagName !== "LI") continue;
      cursor.insertAdjacentElement("afterend", child);
      cursor = child;
    }
    nested.remove();
  }
  // Derived, never restated: the signature screen warns from the same set (#1320). "B"/"I"
  // are rewritten just above, so they are not part of what survives here.
  const allowed = new Set(
    [...EDITOR_VOCABULARY].map((tag) => tag.toUpperCase()).filter((tag) => tag !== "B" && tag !== "I"),
  );
  const dangerous = new Set(["SCRIPT", "STYLE", "IMG", "SVG", "IFRAME", "OBJECT", "EMBED"]);
  for (const element of Array.from(document.body.querySelectorAll("*"))) {
    if (!element.isConnected) continue;
    if (element.tagName === "B" || element.tagName === "I") {
      const replacement = document.createElement(element.tagName === "B" ? "strong" : "em");
      replacement.append(...Array.from(element.childNodes));
      element.replaceWith(replacement);
      continue;
    }
    if (!allowed.has(element.tagName)) {
      if (dangerous.has(element.tagName)) element.remove();
      else element.replaceWith(...Array.from(element.childNodes));
      continue;
    }
    const href = element.tagName === "A" ? element.getAttribute("href") ?? "" : "";
    for (const attribute of Array.from(element.attributes)) element.removeAttribute(attribute.name);
    if (element.tagName === "A") {
      if (!isSafeHttpUrl(href)) {
        element.replaceWith(...Array.from(element.childNodes));
      } else {
        element.setAttribute("href", new URL(href).href);
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noopener noreferrer");
      }
    }
  }
  for (const duplicate of Array.from(document.body.querySelectorAll("strong strong, em em"))) {
    duplicate.replaceWith(...Array.from(duplicate.childNodes));
  }
  return document.body.innerHTML || "<p></p>";
}

/** Mirror the server's canonical multipart text projection for change detection and preview. */
function canonicalTextFromHtml(html: string): string {
  const document = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const inlineText = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.nodeValue ?? "")
        .replace(/\u00a0/g, " ")
        .replace(/[\t\r\n\f\v ]+/g, " ");
    }
    if (!(node instanceof Element)) return "";
    if (node.tagName === "BR") return "\n";
    const label = Array.from(node.childNodes).map(inlineText).join("");
    if (node.tagName !== "A") return label;
    const href = node.getAttribute("href") ?? "";
    if (!href || label.trim() === href || label.trim() === href.replace(/\/$/, "")) return label;
    return label.trim() ? `${label} (${href})` : href;
  };
  const blocks: string[] = [];
  for (const child of Array.from(document.body.children)) {
    if (child.tagName === "UL" || child.tagName === "OL") {
      const ordered = child.tagName === "OL";
      const items = Array.from(child.children)
        .filter((item) => item.tagName === "LI")
        .map((item, index) => `${ordered ? `${index + 1}.` : "-"} ${inlineText(item).trim()}`);
      blocks.push(items.join("\n"));
    } else {
      blocks.push(inlineText(child).replace(/^[ \n]+|[ \n]+$/g, ""));
    }
  }
  return blocks.join("\n\n");
}

function editorSnapshot(editor: LexicalEditor): Omit<RichTextEditorValue, "changed"> {
  const html = normalizeExportedHtml($generateHtmlFromNodes(editor));
  return {
    html,
    text: canonicalTextFromHtml(html),
  };
}

function EditorChangePlugin({ onChange }: { onChange: (value: RichTextEditorValue) => void }) {
  const [editor] = useLexicalComposerContext();
  const baseline = React.useRef<string | null>(null);

  React.useEffect(() => {
    const publish = (changedState = editor.getEditorState()) => {
      changedState.read(() => {
        const snapshot = editorSnapshot(editor);
        if (baseline.current === null) baseline.current = snapshot.html;
        onChange({ ...snapshot, changed: snapshot.html !== baseline.current });
      });
    };
    publish();
    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
      if (!dirtyElements.size && !dirtyLeaves.size) return;
      publish(editorState);
    });
  }, [editor, onChange]);

  return null;
}

/** Remove every text format outside the approved bold/italic vocabulary, including paste styles. */
function ClosedVocabularyPlugin() {
  const [editor] = useLexicalComposerContext();
  React.useEffect(() => editor.registerNodeTransform(TextNode, (node) => {
    if (!$isTextNode(node)) return;
    if (node.getStyle()) node.setStyle("");
    for (const format of [
      "underline", "strikethrough", "code", "highlight", "subscript", "superscript",
    ] as const) {
      if (node.hasFormat(format)) node.toggleFormat(format);
    }
  }), [editor]);
  return null;
}

function activeListType(selection: RangeSelection): "bullet" | "number" | null {
  let node = selection.anchor.getNode();
  while (node.getParent() && node.getParent()?.getType() !== "root") node = node.getParent()!;
  return node instanceof ListNode ? (node.getListType() === "number" ? "number" : "bullet") : null;
}

function ToolbarButton({
  label,
  pressed,
  disabled,
  children,
  onActivate,
}: {
  label: string;
  pressed?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onActivate: () => void;
}) {
  return (
    <button
      type="button"
      className="iconbtn"
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onActivate}
    >
      {children}
    </button>
  );
}

function RichTextToolbar() {
  const t = useT();
  const [editor] = useLexicalComposerContext();
  const [bold, setBold] = React.useState(false);
  const [italic, setItalic] = React.useState(false);
  const [listType, setListType] = React.useState<"bullet" | "number" | null>(null);
  const [hasRange, setHasRange] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkError, setLinkError] = React.useState(false);
  const savedSelection = React.useRef<RangeSelection | null>(null);
  const linkInput = React.useRef<HTMLInputElement | null>(null);

  const readSelection = React.useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      setBold(false);
      setItalic(false);
      setListType(null);
      setHasRange(false);
      return;
    }
    setBold(selection.hasFormat("bold"));
    setItalic(selection.hasFormat("italic"));
    setListType(activeListType(selection));
    setHasRange(!selection.isCollapsed());
  }, []);

  const openLink = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) return;
      savedSelection.current = selection.clone();
      setLinkUrl("");
      setLinkError(false);
      setLinkOpen(true);
      window.requestAnimationFrame(() => linkInput.current?.focus());
    });
  }, [editor]);

  React.useEffect(() => mergeRegister(
    editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
      readSelection();
      return false;
    }, COMMAND_PRIORITY_LOW),
    editor.registerCommand(CAN_UNDO_COMMAND, (value) => {
      setCanUndo(value);
      return false;
    }, COMMAND_PRIORITY_LOW),
    editor.registerCommand(CAN_REDO_COMMAND, (value) => {
      setCanRedo(value);
      return false;
    }, COMMAND_PRIORITY_LOW),
    editor.registerCommand(KEY_DOWN_COMMAND, (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return false;
      event.preventDefault();
      openLink();
      return true;
    }, COMMAND_PRIORITY_LOW),
  ), [editor, openLink, readSelection]);

  const toggleList = (type: "bullet" | "number") => {
    editor.dispatchCommand(
      listType === type
        ? REMOVE_LIST_COMMAND
        : type === "bullet" ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
      undefined,
    );
  };

  const applyLink = () => {
    const candidate = linkUrl.trim();
    if (!isSafeHttpUrl(candidate) || !savedSelection.current) {
      setLinkError(true);
      return;
    }
    editor.update(() => $setSelection(savedSelection.current!.clone()));
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
      url: new URL(candidate).href,
      target: "_blank",
      rel: "noopener noreferrer",
    });
    setLinkOpen(false);
    savedSelection.current = null;
    editor.focus();
  };

  return (
    <div className={reviewStyles.richToolbarArea}>
      <div className={reviewStyles.richToolbar} role="toolbar" aria-label={t("ws.richText.toolbar")}>
        <ToolbarButton label={t("ws.richText.bold")} pressed={bold} onActivate={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}>
          <strong aria-hidden="true">B</strong>
        </ToolbarButton>
        <ToolbarButton label={t("ws.richText.italic")} pressed={italic} onActivate={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}>
          <em aria-hidden="true">I</em>
        </ToolbarButton>
        <ToolbarButton label={t("ws.richText.bullets")} pressed={listType === "bullet"} onActivate={() => toggleList("bullet")}>
          <span aria-hidden="true">•</span>
        </ToolbarButton>
        <ToolbarButton label={t("ws.richText.numbering")} pressed={listType === "number"} onActivate={() => toggleList("number")}>
          <span aria-hidden="true">1.</span>
        </ToolbarButton>
        <ToolbarButton label={t("ws.richText.link")} disabled={!hasRange} onActivate={openLink}>
          <span aria-hidden="true">↗</span>
        </ToolbarButton>
        <span className={reviewStyles.richToolbarDivider} aria-hidden="true" />
        <ToolbarButton label={t("ws.richText.undo")} disabled={!canUndo} onActivate={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}>
          <span aria-hidden="true">↶</span>
        </ToolbarButton>
        <ToolbarButton label={t("ws.richText.redo")} disabled={!canRedo} onActivate={() => editor.dispatchCommand(REDO_COMMAND, undefined)}>
          <span aria-hidden="true">↷</span>
        </ToolbarButton>
      </div>
      {linkOpen && (
        <div className={reviewStyles.richLinkEditor}>
          <label htmlFor="draft-link-url">{t("ws.richText.linkUrl")}</label>
          <input
            ref={linkInput}
            id="draft-link-url"
            className="ribbon-field"
            type="url"
            inputMode="url"
            value={linkUrl}
            aria-invalid={linkError || undefined}
            aria-describedby={linkError ? "draft-link-error" : undefined}
            placeholder="https://"
            onChange={(event) => { setLinkUrl(event.currentTarget.value); setLinkError(false); }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") { event.preventDefault(); applyLink(); }
              if (event.key === "Escape") { event.preventDefault(); setLinkOpen(false); editor.focus(); }
            }}
          />
          <button type="button" className="neo-btn neo-btn--secondary" onClick={applyLink}>{t("ws.richText.applyLink")}</button>
          <button type="button" className="neo-btn neo-btn--ghost" onClick={() => { setLinkOpen(false); editor.focus(); }}>{t("common.cancel")}</button>
          {linkError && <span id="draft-link-error" role="alert">{t("ws.richText.linkError")}</span>}
        </div>
      )}
    </div>
  );
}

export function RichTextEditor({
  initialHtml,
  initialText,
  onChange,
  autoFocus = true,
  ariaLabel,
  placeholder,
  testId = "draft-rich-text-editor",
  namespace = "ReviewedOutboundDraft",
}: {
  initialHtml: string | null | undefined;
  initialText: string;
  onChange: (value: RichTextEditorValue) => void;
  /** Off outside the reply editor: a settings page must not pull the caret in on load (#1320). */
  autoFocus?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  testId?: string;
  /** Lexical treats a shared namespace as "same editor" when pasting; keep them distinct. */
  namespace?: string;
}) {
  const t = useT();
  const initialConfig = React.useMemo(() => ({
    namespace,
    nodes: [TextNode, LinkNode, ListNode, ListItemNode],
    theme: {
      paragraph: reviewStyles.richParagraph,
      link: reviewStyles.richLink,
      list: {
        ul: reviewStyles.richBulletList,
        ol: reviewStyles.richNumberList,
        listitem: reviewStyles.richListItem,
      },
      text: {
        bold: reviewStyles.richBold,
        italic: reviewStyles.richItalic,
      },
    },
    onError(error: Error) { throw error; },
    editorState(editor: LexicalEditor) {
      const source = initialHtml?.trim() || plainTextHtml(initialText);
      const dom = new DOMParser().parseFromString(source, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      if (nodes.length) root.append(...nodes);
      else root.append($createParagraphNode());
    },
  }), [initialHtml, initialText, namespace]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={reviewStyles.richEditorShell} data-testid={testId}>
        <RichTextToolbar />
        <div className={reviewStyles.richEditorBody}>
          <RichTextPlugin
            contentEditable={(
              <ContentEditable
                className={reviewStyles.richContentEditable}
                aria-label={ariaLabel ?? t("ws.richText.editorLabel")}
                autoFocus={autoFocus}
              />
            )}
            placeholder={<div className={reviewStyles.richPlaceholder}>{placeholder ?? t("ws.richText.placeholder")}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>
      <HistoryPlugin />
      <ListPlugin hasStrictIndent />
      <LinkPlugin
        validateUrl={isSafeHttpUrl}
        attributes={{ target: "_blank", rel: "noopener noreferrer" }}
      />
      <ClosedVocabularyPlugin />
      <EditorChangePlugin onChange={onChange} />
    </LexicalComposer>
  );
}
